import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Calendar,
  CircleCheck,
  FileDiff,
  FileSignature,
  ImageIcon,
  ImageOff,
  Loader2,
  MessageSquare,
  Receipt,
  Send,
  ShieldCheck,
  Sparkles,
  ZoomIn,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Toaster, toast } from 'sonner';
import {
  CLIENT_PORTAL_KEYS,
  fetchPortalConfig,
  fetchPortalMessages,
  fetchPortalSignatures,
  fetchPortalPhotos,
  fetchJobsForPortal,
  fetchEstimatesForPortal,
  findPortalByCode,
  buildClientProjectSummary,
  sendMessage,
  markMessagesRead,
  recordSignature,
  loadPortalByCode,
  sendPortalMessage,
  recordPortalSignature,
  signPortalChangeOrder,
  markPortalRead,
  formatUsd,
} from '@/lib/clientPortalData';
import { resolvePortalPhotos } from '@/lib/portalStorage';

/**
 * Signature image that copes with every stored form: inline data URLs and
 * absolute URLs render directly; a private-bucket storage path
 * (client/<id>/signatures/…) is resolved to a short-lived signed URL via the
 * portal-photo-url edge function (validated by this portal's code).
 */
const SignatureImage = ({ value, code, className = '' }) => {
  const [src, setSrc] = React.useState(
    value && (String(value).startsWith('data:') || String(value).startsWith('http'))
      ? value
      : null
  );
  React.useEffect(() => {
    let alive = true;
    const v = String(value || '');
    if (!v) return undefined;
    if (v.startsWith('data:') || v.startsWith('http')) {
      setSrc(v);
      return undefined;
    }
    resolvePortalPhotos(code, 'client', [{ path: v }]).then((list) => {
      if (alive && list?.[0]?.url) setSrc(list[0].url);
    });
    return () => {
      alive = false;
    };
  }, [value, code]);
  if (!src) return null;
  return (
    <img
      alt="Signature"
      src={src}
      className={className}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};
import { JOB_PHASES } from '@/lib/jobPhases';
import ClientProposalView from '@/components/estimates/ClientProposalView';
import { resolveBranding } from '@/lib/branding';

/* -------------------------------------------------------------------------
 * Code-entry screen
 * ----------------------------------------------------------------------- */
const CodeEntry = ({ value, setValue, onSubmit, error, busy }) => {
  const inputRef = useRef(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const handleChange = (e) => {
    // Codes are alphanumeric now (new: 6-char; legacy: 4–8 digit). Uppercase and
    // keep only code characters. Auto-submit a full 8-char legacy code; new or
    // shorter codes submit via Enter or the button.
    const v = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 8);
    setValue(v);
    if (v.length === 8) onSubmit(v);
  };
  return (
    <div
      className="min-h-[100dvh] flex flex-col items-center justify-center px-4 bg-[hsl(var(--background))] text-foreground"
      data-testid="client-portal-code-entry"
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8 px-2">
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
            Contractor Command
          </div>
          <h1
            className="mt-2 text-2xl sm:text-4xl tracking-[0.1em] sm:tracking-[0.18em] uppercase text-foreground break-words leading-tight"
            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontWeight: 700 }}
          >
            K.O. CO+RE Systems
          </h1>
          <div className="mt-4 text-sm text-muted-foreground">
            Enter your access code to view your project.
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (value.length >= 4) onSubmit(value);
          }}
          noValidate
        >
          <Input
            ref={inputRef}
            value={value}
            onChange={handleChange}
            inputMode="text"
            autoComplete="one-time-code"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Access code"
            className="h-16 text-center text-3xl tracking-[0.4em] font-mono bg-[hsl(var(--card))] border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] focus-visible:ring-[hsl(var(--primary))]/40"
            maxLength={8}
            data-testid="client-portal-code-input"
            aria-label="Access code"
          />
          <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
            <ShieldCheck size={12} className="text-[hsl(var(--success))]" />
            Secure project view
          </div>
          {error ? (
            <Alert
              className="mt-4 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
              data-testid="client-portal-code-error"
            >
              <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          ) : null}
          <Button
            type="submit"
            disabled={busy || value.length < 4}
            className="mt-5 w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.18em] text-xs"
            data-testid="client-portal-code-submit"
          >
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
            Enter portal
          </Button>
        </form>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Project tab
 * ----------------------------------------------------------------------- */
const ProjectTab = ({ summary }) => {
  if (!summary) return null;
  const job = summary.jobs[0]; // Show primary job; aggregate KPIs include all
  const phaseIdx = Math.min(JOB_PHASES.length - 1, Math.max(0, Number(job?.phase) || 0));
  const phase = JOB_PHASES[phaseIdx];
  const pct = Math.round(((phaseIdx + 1) / JOB_PHASES.length) * 100);
  const collectedPct =
    summary.revenue > 0 ? Math.round((summary.collected / summary.revenue) * 100) : 0;
  return (
    <div className="space-y-4" data-testid="client-portal-project-tab">
      {/* Hero */}
      {job ? (
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-4 sm:p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Your project
            </div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
              {job.name}
            </h2>
            <div className="text-xs text-muted-foreground mt-0.5">{job.address}</div>

            <div className="mt-4">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                <span>Current phase</span>
                <span className="text-foreground font-semibold">{phase?.label || `Phase ${phaseIdx}`}</span>
              </div>
              <Progress
                value={pct}
                className="mt-2 h-2 bg-[hsl(var(--muted))]"
                data-testid="client-portal-progress"
              />
              <div className="mt-1 text-[11px] text-muted-foreground">
                {pct}% of {JOB_PHASES.length} phases
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Contract total — reflects approved change orders (uniform with the
          proposal's Investment summary). */}
      {summary.revenue > 0 ? (
        <Card
          className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
          data-testid="client-portal-contract-total"
        >
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Contract total
            </div>
            <div className="mt-1 text-2xl font-bold text-[hsl(var(--primary))] tabular-nums">
              {formatUsd(summary.revenue)}
            </div>
            {summary.co_total > 0 ? (
              <div className="mt-1 text-[11px] text-muted-foreground tabular-nums">
                Original {formatUsd(summary.contract)}
                {' · '}
                <span className="text-[hsl(var(--success))]">
                  Approved changes +{formatUsd(summary.co_total)}
                </span>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Money summary */}
      <div className="grid grid-cols-2 gap-3" data-testid="client-portal-money">
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Collected
            </div>
            <div className="mt-1 text-xl font-semibold text-[hsl(var(--success))] tabular-nums">
              {formatUsd(summary.collected)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {collectedPct}% of contract
            </div>
          </CardContent>
        </Card>
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Outstanding
            </div>
            <div className="mt-1 text-xl font-semibold text-foreground tabular-nums">
              {formatUsd(summary.outstanding)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {summary.overdue > 0 ? `${formatUsd(summary.overdue)} past due` : 'All on track'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Phase timeline */}
      {job ? (
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
              Project timeline
            </div>
            <div className="space-y-2">
              {JOB_PHASES.map((p, i) => {
                const done = i < phaseIdx;
                const current = i === phaseIdx;
                return (
                  <div
                    key={p.key}
                    className={cn(
                      'flex items-center gap-3 rounded-md border px-3 py-2',
                      current
                        ? 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5'
                        : 'border-border bg-[hsl(var(--muted))]/40'
                    )}
                  >
                    <div
                      className={cn(
                        'h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold tabular-nums',
                        done
                          ? 'bg-[hsl(var(--success))]/20 text-[hsl(var(--success))]'
                          : current
                          ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))]'
                          : 'bg-[hsl(var(--muted))] text-muted-foreground'
                      )}
                    >
                      {done ? <CircleCheck size={12} /> : i + 1}
                    </div>
                    <div className="text-sm text-foreground">{p.label}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Invoices tab
 * ----------------------------------------------------------------------- */
const InvoicesTab = ({ summary }) => {
  if (!summary) return null;
  const STATUS_BADGE = {
    paid: 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
    overdue:
      'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
    due: 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
    upcoming: 'bg-[hsl(var(--muted))] text-foreground border-border',
  };
  const STATUS_LABEL = {
    paid: 'Paid',
    overdue: 'Overdue',
    due: 'Due',
    upcoming: 'Upcoming',
  };
  return (
    <div data-testid="client-portal-invoices-tab">
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-3 sm:p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2 px-1">
            Invoice schedule
          </div>
          {summary.invoices.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No invoices on file yet.
            </div>
          ) : (
            <div data-testid="client-portal-invoices-list">
              {summary.invoices.map((inv) => (
                <div
                  key={`${inv.job_id}-${inv.id}`}
                  className="flex items-center justify-between gap-3 border-b border-border px-2 py-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">
                      {inv.desc || 'Invoice'}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      Due {inv.due || 'TBD'}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-foreground tabular-nums">
                      {formatUsd(inv.amount)}
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        'mt-0.5 uppercase text-[10px] tracking-[0.16em]',
                        STATUS_BADGE[inv.status]
                      )}
                    >
                      {STATUS_LABEL[inv.status]}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Proposal tab — signature canvas
 * ----------------------------------------------------------------------- */
const SignatureCanvas = ({ onChange, value }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  const getCtx = () => ref.current?.getContext('2d');

  const start = (x, y) => {
    drawing.current = true;
    last.current = { x, y };
  };
  const move = (x, y) => {
    if (!drawing.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    ctx.strokeStyle = '#CEB584';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    last.current = { x, y };
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (ref.current) onChange?.(ref.current.toDataURL('image/png'));
  };

  const pos = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return [0, 0];
    if (e.touches?.[0]) {
      return [e.touches[0].clientX - r.left, e.touches[0].clientY - r.top];
    }
    return [e.clientX - r.left, e.clientY - r.top];
  };

  const clear = () => {
    const ctx = getCtx();
    if (!ctx || !ref.current) return;
    ctx.clearRect(0, 0, ref.current.width, ref.current.height);
    onChange?.(null);
  };

  return (
    <div>
      <div className="rounded-md border border-dashed border-[hsl(var(--primary))]/40 bg-[hsl(var(--muted))]/40 p-2 relative">
        <canvas
          ref={ref}
          width={520}
          height={140}
          className="w-full h-[140px] touch-none rounded-sm bg-[hsl(var(--background))]"
          data-testid="client-portal-signature-canvas"
          onMouseDown={(e) => {
            const [x, y] = pos(e);
            start(x, y);
          }}
          onMouseMove={(e) => {
            const [x, y] = pos(e);
            move(x, y);
          }}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={(e) => {
            e.preventDefault();
            const [x, y] = pos(e);
            start(x, y);
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            const [x, y] = pos(e);
            move(x, y);
          }}
          onTouchEnd={end}
        />
        {!value ? (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-[11px] text-muted-foreground tracking-[0.18em] uppercase">
            Sign with finger or mouse
          </div>
        ) : null}
      </div>
      <div className="mt-2 flex justify-end">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={clear}
          className="text-muted-foreground hover:text-foreground"
          data-testid="client-portal-signature-clear"
        >
          Clear signature
        </Button>
      </div>
    </div>
  );
};

const ProposalTab = ({ summary, signatures, onSign, busy, code, clientId }) => {
  const [name, setName] = useState('');
  const [sigUrl, setSigUrl] = useState(null);
  const [pickedId, setPickedId] = useState(null);

  const estimates = summary?.estimates || [];
  const active = estimates.find((e) => e.id === pickedId) || estimates[0] || null;

  useEffect(() => {
    if (!pickedId && active?.id) setPickedId(active.id);
  }, [active?.id, pickedId]);

  // Approved change orders on the active estimate's job — surfaced on the
  // proposal so the client sees the revised contract total after signing one.
  const activeJob = (summary?.jobs || []).find((j) => j.id === active?.job_id);
  const activeJobCos = (
    Array.isArray(activeJob?.change_orders) ? activeJob.change_orders : []
  ).filter((co) => (co?.status || '') === 'approved');

  const sigList = (signatures?.[summary?.client_id] || []).slice().sort((a, b) =>
    (b.signed_at || '').localeCompare(a.signed_at || '')
  );

  const alreadySigned = sigList.find(
    (s) => s.estimate_id === active?.id || (active && !s.estimate_id && active.title === s.estimate_title)
  );

  const handleSign = async () => {
    if (!active) {
      toast.error('No estimate to sign yet.');
      return;
    }
    if (!name.trim()) {
      toast.error('Type your full legal name.');
      return;
    }
    await onSign({
      estimateId: active.id,
      jobId: active.job_id,
      estimateTitle: active.title || 'Estimate',
      total: active.total,
      signedName: name.trim(),
      signatureDataUrl: sigUrl,
      clientId,
    });
    setName('');
    setSigUrl(null);
  };

  if (estimates.length === 0) {
    return (
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="client-portal-proposal-tab">
        <CardContent className="p-6 text-center">
          <FileSignature size={20} className="mx-auto text-muted-foreground mb-2" />
          <div className="text-sm text-muted-foreground">
            Your contractor hasn't shared a proposal here yet.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-portal-proposal-tab">
      {/* Estimate picker if multiple */}
      {estimates.length > 1 ? (
        <div className="flex flex-wrap gap-2">
          {estimates.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => setPickedId(e.id)}
              className={cn(
                'rounded-md border px-3 py-1.5 text-xs uppercase tracking-[0.16em] transition-colors',
                e.id === active?.id
                  ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
                  : 'border-border bg-[hsl(var(--muted))] text-muted-foreground hover:text-foreground'
              )}
            >
              {e.title || 'Estimate'}
            </button>
          ))}
        </div>
      ) : null}

      {active ? (
        <ClientProposalView
          estimate={active}
          clientName={
            summary?.client?.name ||
            summary?.contact?.name ||
            active?.client_name ||
            ''
          }
          changeOrders={activeJobCos}
        />
      ) : null}

      {/* Signature box */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4 sm:p-5">
          {alreadySigned ? (
            <div className="flex items-start gap-3 rounded-md border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 px-3 py-3">
              <CircleCheck className="text-[hsl(var(--success))] mt-0.5" size={16} />
              <div>
                <div className="text-sm font-semibold text-foreground">
                  Signed by {alreadySigned.signed_name}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {new Date(alreadySigned.signed_at).toLocaleString()} {'\u00b7'}{' '}
                  {alreadySigned.estimate_title}
                </div>
                {alreadySigned.signature_url ? (
                  <SignatureImage
                    value={alreadySigned.signature_url}
                    code={code}
                    className="mt-2 max-h-24 rounded border border-border bg-[hsl(var(--background))]"
                  />
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Sign and accept
              </div>
              <div className="mt-2 grid grid-cols-1 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Type your full legal name
                  </label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Casey Cohen"
                    className="h-10 mt-1 bg-[hsl(var(--muted))] border-border text-foreground"
                    data-testid="client-portal-signed-name"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Draw signature
                  </label>
                  <div className="mt-1">
                    <SignatureCanvas value={sigUrl} onChange={setSigUrl} />
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={handleSign}
                  disabled={busy || !name.trim()}
                  className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.18em] text-xs"
                  data-testid="client-portal-sign-submit"
                >
                  {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <FileSignature size={14} className="mr-2" />}
                  Sign and accept proposal
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  By signing you agree to the proposal as written. Your contractor will be notified instantly.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Change Orders tab — review + sign pending change orders
 * ----------------------------------------------------------------------- */
const CO_TYPE_BADGE = {
  fixed: 'bg-[hsl(var(--muted))] text-muted-foreground border-border',
  tm: 'bg-[hsl(var(--info))]/12 text-[hsl(var(--info))] border-[hsl(var(--info))]/30',
};
const CO_STATUS_BADGE = {
  pending:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  approved:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
};

const ChangeOrdersTab = ({ summary, onSign, busy }) => {
  const jobs = summary?.jobs || [];
  const multiJob = jobs.length > 1;
  const rows = [];
  for (const j of jobs) {
    const cos = Array.isArray(j.change_orders) ? j.change_orders : [];
    for (const co of cos) rows.push({ ...co, jobId: j.id, jobName: j.name });
  }
  const pending = rows.filter((c) => (c.status || 'pending') === 'pending');
  const approved = rows.filter((c) => c.status === 'approved');

  const [openId, setOpenId] = useState(null);
  const [name, setName] = useState('');
  const [sigUrl, setSigUrl] = useState(null);

  const money = (n) => formatUsd(Number(n) || 0);

  const renderMeta = (co) => {
    const isTM = (co.type || 'fixed') === 'tm';
    if (!isTM) return money(co.amount);
    const hasCap = co.nte !== null && co.nte !== undefined && co.nte !== '';
    return `${money(co.amount)} est. · ${hasCap ? `cap ${money(co.nte)}` : 'no cap'}`;
  };

  const startReview = (id) => {
    setOpenId((cur) => (cur === id ? null : id));
    setName('');
    setSigUrl(null);
  };

  const submit = async (row) => {
    if (!name.trim()) {
      toast.error('Type your full legal name to approve.');
      return;
    }
    const ok = await onSign({
      jobId: row.jobId,
      coId: row.id,
      signedName: name.trim(),
      // The canvas produces a data: URL, which the RPC rejects to keep the
      // settings row small. The typed name + server timestamp is the binding
      // record; the drawing is a signing affordance only.
      signatureUrl:
        sigUrl && !String(sigUrl).startsWith('data:') ? sigUrl : null,
    });
    if (ok) {
      setOpenId(null);
      setName('');
      setSigUrl(null);
    }
  };

  if (rows.length === 0) {
    return (
      <Card
        className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
        data-testid="client-portal-change-orders-tab"
      >
        <CardContent className="p-6 text-center">
          <FileDiff size={20} className="mx-auto text-muted-foreground mb-2" />
          <div className="text-sm text-muted-foreground">
            No change orders yet. If your contractor proposes a change or add-on,
            it will show up here for your approval.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-portal-change-orders-tab">
      {pending.length > 0 ? (
        <Card className="border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5">
          <CardContent className="p-3 sm:p-4 flex items-center gap-2">
            <AlertCircle size={15} className="text-[hsl(var(--warning))] shrink-0" />
            <div className="text-sm text-foreground">
              {pending.length === 1
                ? '1 change order is waiting for your approval.'
                : `${pending.length} change orders are waiting for your approval.`}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Pending — reviewable + signable */}
      {pending.map((co) => {
        const isTM = (co.type || 'fixed') === 'tm';
        const isOpen = openId === co.id;
        return (
          <Card
            key={`${co.jobId}-${co.id}`}
            className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
            data-testid={`client-portal-co-${co.id}`}
          >
            <CardContent className="p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-foreground">
                    {co.desc}
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      'uppercase text-[10px] tracking-[0.14em]',
                      CO_TYPE_BADGE[isTM ? 'tm' : 'fixed']
                    )}
                  >
                    {isTM ? 'T&M' : 'Fixed'}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn(
                      'uppercase text-[10px] tracking-[0.14em]',
                      CO_STATUS_BADGE.pending
                    )}
                  >
                    Pending
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1 tabular-nums">
                  {renderMeta(co)}
                </div>
                {multiJob ? (
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {co.jobName}
                  </div>
                ) : null}
              </div>

              {!isOpen ? (
                <Button
                  type="button"
                  onClick={() => startReview(co.id)}
                  className="mt-3 w-full h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                  data-testid={`client-portal-co-review-${co.id}`}
                >
                  <FileSignature size={14} className="mr-2" />
                  Review &amp; sign
                </Button>
              ) : (
                <div className="mt-4 border-t border-border pt-4 space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      Type your full legal name
                    </label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Casey Cohen"
                      className="h-10 mt-1 bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="client-portal-co-signed-name"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      Draw signature
                    </label>
                    <div className="mt-1">
                      <SignatureCanvas value={sigUrl} onChange={setSigUrl} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setOpenId(null)}
                      disabled={busy}
                      className="h-11 border-border text-muted-foreground hover:text-foreground uppercase tracking-[0.16em] text-xs"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={() => submit(co)}
                      disabled={busy || !name.trim()}
                      className="flex-1 h-11 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                      data-testid={`client-portal-co-approve-${co.id}`}
                    >
                      {busy ? (
                        <Loader2 size={14} className="mr-2 animate-spin" />
                      ) : (
                        <FileSignature size={14} className="mr-2" />
                      )}
                      Approve{isTM ? '' : ` ${money(co.amount)}`}
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Approving adds this{' '}
                    {isTM ? 'time & material item' : 'amount'} to your contract.
                    Your contractor is notified instantly.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Approved — record */}
      {approved.length > 0 ? (
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-3 sm:p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2 px-1">
              Approved changes
            </div>
            <div>
              {approved.map((co) => {
                const isTM = (co.type || 'fixed') === 'tm';
                return (
                  <div
                    key={`${co.jobId}-${co.id}`}
                    className="flex items-start justify-between gap-3 border-b border-border px-2 py-3 last:border-0"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground">
                          {co.desc}
                        </span>
                        <Badge
                          variant="outline"
                          className={cn(
                            'uppercase text-[10px] tracking-[0.14em]',
                            CO_TYPE_BADGE[isTM ? 'tm' : 'fixed']
                          )}
                        >
                          {isTM ? 'T&M' : 'Fixed'}
                        </Badge>
                      </div>
                      {co.signed_name ? (
                        <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                          <CircleCheck
                            size={11}
                            className="text-[hsl(var(--success))] shrink-0"
                          />
                          Signed by {co.signed_name}
                          {co.signed_at
                            ? ` · ${new Date(
                                co.signed_at
                              ).toLocaleDateString()}`
                            : ''}
                        </div>
                      ) : (
                        <div className="text-[11px] text-muted-foreground mt-1">
                          Approved
                        </div>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-foreground tabular-nums shrink-0">
                      {money(co.amount)}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Photos tab
 * ----------------------------------------------------------------------- */
const PhotosTab = ({ photos = [] }) => {
  const [open, setOpen] = useState(null);
  if (!photos.length) {
    return (
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="client-portal-photos-tab">
        <CardContent className="p-6 text-center">
          <ImageOff size={20} className="mx-auto text-muted-foreground mb-2" />
          <div className="text-sm text-muted-foreground">
            No photos shared yet. Your contractor will push photos here as work progresses.
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <div data-testid="client-portal-photos-tab">
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-3 sm:p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {photos.map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => setOpen(p)}
                className="relative aspect-square rounded-md border border-border overflow-hidden hover:ring-2 hover:ring-[hsl(var(--primary))]/40 transition-all group"
                data-testid={`client-portal-photo-${p.id}`}
              >
                <img
                  src={p.url}
                  alt={p.caption || 'Project photo'}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn size={20} className="text-white" />
                </div>
                {p.caption ? (
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/70 text-[10px] text-white truncate">
                    {p.caption}
                  </div>
                ) : null}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
      {open ? (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
          data-testid="client-portal-photo-modal"
          role="dialog"
          aria-modal="true"
        >
          <img
            src={open.url}
            alt={open.caption || 'Project photo'}
            className="max-h-[90vh] max-w-[95vw] rounded-md shadow-2xl border border-border"
          />
        </div>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Messages tab
 * ----------------------------------------------------------------------- */
const MessagesTab = ({ messages = [], onSend, sending }) => {
  const [body, setBody] = useState('');
  const scrollRef = useRef(null);
  useEffect(() => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  }, [messages.length]);
  const handle = async () => {
    if (!body.trim()) return;
    await onSend(body.trim());
    setBody('');
  };
  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="client-portal-messages-tab">
      <CardContent className="p-3 sm:p-4">
        <div
          ref={scrollRef}
          className="space-y-2 max-h-[55vh] overflow-auto kc-scroll pr-1 mb-3"
          data-testid="client-portal-messages-list"
        >
          {messages.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <MessageSquare size={18} className="mx-auto mb-2 opacity-50" />
              Send your contractor a message.
            </div>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={cn(
                  'rounded-md border px-3 py-2 max-w-[80%]',
                  m.from === 'client'
                    ? 'ml-auto border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10'
                    : 'border-border bg-[hsl(var(--muted))]/40'
                )}
              >
                <div className="text-sm text-foreground whitespace-pre-wrap">{m.body}</div>
                <div className="mt-1 text-[10px] text-muted-foreground">
                  {m.from === 'client' ? 'You' : 'Contractor'} {'\u00b7'}{' '}
                  {m.sent_at ? new Date(m.sent_at).toLocaleString() : ''}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="flex items-end gap-2">
          <Textarea
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 bg-[hsl(var(--muted))] border-border text-foreground resize-none"
            data-testid="client-portal-message-input"
          />
          <Button
            type="button"
            onClick={handle}
            disabled={sending || !body.trim()}
            className="h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
            data-testid="client-portal-message-send"
          >
            {sending ? <Loader2 size={12} className="mr-2 animate-spin" /> : <Send size={12} className="mr-2" />}
            Send
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* -------------------------------------------------------------------------
 * Main view
 * ----------------------------------------------------------------------- */
export default function ClientPortalView() {
  const params = useParams();
  const navigate = useNavigate();

  const [code, setCode] = useState(params.code || '');
  const [config, setConfig] = useState(null);
  // White-label company branding from the portal RPC (migration 025).
  const [company, setCompany] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [allPortals, setAllPortals] = useState({});
  const [allMessages, setAllMessages] = useState({});
  const [allPhotos, setAllPhotos] = useState({});
  const [allSignatures, setAllSignatures] = useState({});

  const [signing, setSigning] = useState(false);
  const [signingCo, setSigningCo] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);

  const submitCode = useCallback(async (val) => {
    const v = String(val || code || '').trim().toUpperCase();
    // Codes come in two generations: legacy 4-8 DIGIT codes, and the current
    // 6-char ALPHANUMERIC codes (clientPortalData CODE_ALPHABET). This check
    // used to demand digits only, so every new-style code was rejected right
    // here - before the server was ever asked (live-fire 2026-09-22, a real
    // client code refused at the door). Validate the union; the RPC and its
    // per-IP lockout remain the real gate.
    if (!/^[A-Z0-9]{4,8}$/.test(v)) {
      setError('Enter the access code your contractor sent you.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Single-call portal load via the anon-safe RPC.
      const result = await loadPortalByCode(v);
      if (!result.ok) {
        if (result.error === 'invalid_code') {
          setError('Code not recognized or portal disabled. Double-check with your contractor.');
        } else if (result.error === 'rate_limited') {
          setError('Too many attempts. Please wait a few minutes and try again.');
        } else if (result.error === 'portal_unavailable') {
          setError('The portal is temporarily unavailable. Please try again later.');
        } else {
          setError(result.error || 'Something went wrong. Try again.');
        }
        return;
      }
      setJobs(result.jobs);
      setEstimates(result.estimates);
      setAllMessages({ [result.config.client_id]: result.messages });
      setAllSignatures({ [result.config.client_id]: result.signatures });
      setAllPhotos({ [result.config.client_id]: result.photos });
      setConfig(result.config);
      setCompany(result.company || null);
      setAllPortals({ [result.config.client_id]: result.config });
      setAuthed(true);
      // Mark contractor messages as read for this client (best-effort)
      markPortalRead(v).catch(() => { /* noop */ });
      // Sync URL if needed
      if (params.code !== v) navigate(`/portal/client/${v}`, { replace: true });
    } catch (e) {
      setError(e?.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }, [code, navigate, params.code]);

  // Auto-submit if URL has a code
  useEffect(() => {
    if (params.code && !authed && !loading) {
      submitCode(params.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo(
    () => buildClientProjectSummary(config, jobs, estimates),
    [config, jobs, estimates]
  );

  const myMessages = config ? allMessages[config.client_id] || [] : [];
  const myPhotos = config ? allPhotos[config.client_id] || [] : [];

  // Count change orders still waiting on the client, for the tab badge.
  const pendingCoCount = useMemo(() => {
    let n = 0;
    for (const j of summary?.jobs || []) {
      for (const co of Array.isArray(j.change_orders) ? j.change_orders : []) {
        if ((co.status || 'pending') === 'pending') n += 1;
      }
    }
    return n;
  }, [summary]);

  // White-label branding: accent color overrides --primary for the whole
  // portal (header, tabs, proposal accents all inherit it); brand name/font/
  // logo drive the top header. Falls back to the default look when unset.
  const branding = resolveBranding(company?.branding);
  const brandName = company?.name || 'K.O. CO+RE Systems';
  const brandFontStyle = branding.brandFontFamily
    ? { fontFamily: branding.brandFontFamily }
    : { fontFamily: "'Space Grotesk', system-ui, sans-serif", fontWeight: 700 };

  const handleSign = async (payload) => {
    setSigning(true);
    try {
      const result = await recordPortalSignature(config.code, payload);
      if (!result.ok) {
        toast.error(result.error || 'Could not record signature.');
        return;
      }
      // Append the new signature locally
      setAllSignatures((cur) => {
        const list = cur[config.client_id] || [];
        return { ...cur, [config.client_id]: [...list, result.signature] };
      });
      toast.success('Proposal signed and sent to your contractor');
    } catch (e) {
      toast.error(e?.message || 'Could not record signature.');
    } finally {
      setSigning(false);
    }
  };

  const handleSignChangeOrder = async ({
    jobId,
    coId,
    signedName,
    signatureUrl,
  }) => {
    setSigningCo(true);
    try {
      const result = await signPortalChangeOrder(config.code, {
        jobId,
        coId,
        signedName,
        signatureUrl,
        clientId: config.client_id,
      });
      if (!result.ok) {
        const map = {
          rate_limited: 'Too many attempts. Please wait a moment and try again.',
          invalid_code: 'Session expired. Re-enter your access code.',
          name_required: 'Type your full legal name to approve.',
          change_order_not_pending:
            'This change order was already handled. Refresh to see the latest.',
          job_mismatch: "This change order isn't on your project.",
          job_not_found: 'That job could not be found.',
        };
        toast.error(map[result.error] || 'Could not sign the change order.');
        return false;
      }
      // Reflect the approval locally: stamp the CO and update the job's co_total.
      const nowIso = new Date().toISOString();
      setJobs((cur) =>
        cur.map((j) => {
          if (j.id !== jobId) return j;
          const cos = Array.isArray(j.change_orders) ? j.change_orders : [];
          return {
            ...j,
            co_total:
              typeof result.co_total === 'number' ? result.co_total : j.co_total,
            change_orders: cos.map((co) =>
              co.id === coId
                ? {
                    ...co,
                    status: 'approved',
                    signed_name: signedName,
                    signed_at: nowIso,
                  }
                : co
            ),
          };
        })
      );
      toast.success('Change order approved and sent to your contractor');
      return true;
    } catch (e) {
      toast.error(e?.message || 'Could not sign the change order.');
      return false;
    } finally {
      setSigningCo(false);
    }
  };

  const handleSendMessage = async (body) => {
    setSendingMsg(true);
    try {
      const result = await sendPortalMessage(config.code, body);
      if (!result.ok) {
        toast.error(result.error || 'Could not send message.');
        return;
      }
      setAllMessages((cur) => {
        const list = cur[config.client_id] || [];
        return { ...cur, [config.client_id]: [...list, result.message] };
      });
    } catch (e) {
      toast.error(e?.message || 'Could not send message.');
    } finally {
      setSendingMsg(false);
    }
  };

  const handleLogout = () => {
    setAuthed(false);
    setConfig(null);
    setCode('');
    navigate('/portal/client', { replace: true });
  };

  if (!authed) {
    return (
      <>
        <Toaster theme="light" position="top-right" />
        <CodeEntry
          value={code}
          setValue={setCode}
          onSubmit={submitCode}
          error={error}
          busy={loading}
        />
      </>
    );
  }

  return (
    <div
      className="min-h-[100dvh] bg-[hsl(var(--background))] text-foreground"
      style={branding.containerStyle}
      data-testid="client-portal-view"
    >
      <Toaster theme="light" position="top-right" />
      {/* Top bar */}
      {/* The one dark element: the black-and-gold brand bar. `.dark` here
          scopes the dark tokens to the header; the page below is light. */}
      <div className="safe-area-top-shield" aria-hidden="true" />
      <header className="dark sticky top-0 z-40 pt-safe bg-[hsl(var(--card))] text-foreground border-b border-[hsl(var(--hairline))] overflow-hidden">
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2 sm:gap-3">
          <div className="min-w-0 flex-1 flex items-center gap-2.5">
            {company?.logo_url ? (
              // A stale/unloadable logo value (e.g. a private-bucket URL from
              // an old save) must never show a broken-image glyph to a client
              // — hide it and let the brand name carry the header.
              <img
                src={company.logo_url}
                alt={`${brandName} logo`}
                className="h-9 sm:h-10 w-auto max-w-[120px] object-contain rounded-sm shrink-0"
                data-testid="client-portal-logo"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : null}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.24em] sm:tracking-[0.28em] text-muted-foreground truncate">
                Client portal
              </div>
              <div
                className="text-sm sm:text-lg tracking-[0.08em] sm:tracking-[0.18em] uppercase text-foreground truncate"
                style={brandFontStyle}
                data-testid="client-portal-brand-name"
              >
                {brandName}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 min-w-0">
            <div className="text-right min-w-0 max-w-[110px] sm:max-w-none">
              <div
                className="text-xs font-semibold text-foreground truncate"
                title={config?.client_name || ''}
              >
                {config?.client_name}
              </div>
              <div className="text-[10px] text-muted-foreground tabular-nums">
                Code {config?.code}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="h-8 px-2.5 sm:px-3 border-border text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] uppercase tracking-[0.16em] text-[10px] shrink-0"
              data-testid="client-portal-exit"
            >
              Exit
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-3 py-4">
        <Tabs defaultValue="project" className="w-full" data-testid="client-portal-tabs">
          <TabsList className="bg-[hsl(var(--muted))] border border-border w-full overflow-x-auto no-scrollbar mb-3">
            <TabsTrigger value="project" className="text-xs uppercase tracking-[0.16em]">
              <Sparkles size={12} className="mr-1.5" />
              Project
            </TabsTrigger>
            <TabsTrigger value="invoices" className="text-xs uppercase tracking-[0.16em]">
              <Receipt size={12} className="mr-1.5" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="proposal" className="text-xs uppercase tracking-[0.16em]">
              <FileSignature size={12} className="mr-1.5" />
              Proposal
            </TabsTrigger>
            <TabsTrigger
              value="change_orders"
              className="text-xs uppercase tracking-[0.16em]"
              data-testid="client-portal-tab-change-orders"
            >
              <FileDiff size={12} className="mr-1.5" />
              Changes
              {pendingCoCount > 0 ? (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--warning))] text-[hsl(var(--background))] text-[10px] font-semibold tabular-nums">
                  {pendingCoCount}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="photos" className="text-xs uppercase tracking-[0.16em]">
              <ImageIcon size={12} className="mr-1.5" />
              Photos
            </TabsTrigger>
            <TabsTrigger value="messages" className="text-xs uppercase tracking-[0.16em]">
              <MessageSquare size={12} className="mr-1.5" />
              Messages
            </TabsTrigger>
          </TabsList>

          <TabsContent value="project">
            <ProjectTab summary={summary} />
          </TabsContent>
          <TabsContent value="invoices">
            <InvoicesTab summary={summary} />
          </TabsContent>
          <TabsContent value="proposal">
            <ProposalTab
              summary={summary}
              signatures={allSignatures}
              onSign={handleSign}
              busy={signing}
              code={config?.code}
              clientId={config?.client_id}
            />
          </TabsContent>
          <TabsContent value="change_orders">
            <ChangeOrdersTab
              summary={summary}
              onSign={handleSignChangeOrder}
              busy={signingCo}
            />
          </TabsContent>
          <TabsContent value="photos">
            <PhotosTab photos={myPhotos} />
          </TabsContent>
          <TabsContent value="messages">
            <MessagesTab
              messages={myMessages}
              onSend={handleSendMessage}
              sending={sendingMsg}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
